<div class="onboarding-container">
	<!-- Already complete -->
	@if (step() === 'already-complete') {
		<div class="onboarding-state">
			<h1>Account Already Active</h1>
			<p>Your LanguageBird account is already set up. You can sign in directly.</p>
			<a [href]="loginUrl" class="btn btn--primary">Go to My LanguageBird</a>
			<p class="step-footer">Not what you expected? <a href="https://help.languagebird.com/kb-tickets/new">Contact support</a>.</p>
		</div>
	}

	<!-- Error -->
	@if (step() === 'error') {
		<div class="onboarding-state">
			<h1>Something Went Wrong</h1>
			<p>{{ errorMessage() }}</p>
			<p class="support-text">Need help? Email us at <a href="https://help.languagebird.com/kb-tickets/new">support&#64;languagebird.com</a></p>
		</div>
	}

	<!-- Step 1: Welcome -->
	@if (step() === 'welcome') {
		<div class="onboarding-step">
			<h1>Welcome{{ firstName() ? ', ' + firstName() + '!' : '!' }}</h1>
			<p>Let's get you set up for your first login.<br />This quick process only takes a few steps.</p>
			@if (errorMessage()) {
				<div class="alert alert--error">{{ errorMessage() }}</div>
				<div class="error-actions">
					<a href="https://help.languagebird.com/kb-tickets/new" class="support-link">Contact support</a>
				</div>
			}
			<button type="button" class="btn btn--primary" (click)="nextFromWelcome()" [disabled]="isSubmitting()">
				@if (isSubmitting()) {
					Please wait&hellip;
				} @else {
					Next
				}
			</button>
		</div>
	}

	<!-- Step 2: Show username -->
	@if (step() === 'show-username') {
		<div class="onboarding-step">
			<div class="username-display">{{ username() }}</div>
			<p>This is your LanguageBird username and email.<br />You will use it each time you login.</p>
			<button type="button" class="btn btn--primary" (click)="nextFromShowUsername()">Next</button>
		</div>
	}

	<!-- Step 3: Verify email -->
	@if (step() === 'verify-email') {
		<div class="onboarding-step">
			<p>
				A verification code was sent to your registered contact information:<br /><strong>{{ maskedEmail() }}</strong>
			</p>
			<p>Enter the code below and click next to proceed:</p>

			@if (errorMessage()) {
				<div class="alert alert--error">{{ errorMessage() }}</div>
				@if (canStartOver()) {
					<div class="error-actions">
						<button type="button" class="btn btn--ghost" (click)="requestNewCode()">Send New Code</button>
					</div>
				}
			}

			<form (ngSubmit)="submitVerificationCode()" #form="ngForm">
				<div class="form-field">
					<input
						id="verificationCode"
						type="text"
						name="verificationCode"
						[(ngModel)]="verificationCode"
						autocomplete="one-time-code"
						inputmode="numeric"
						required
					/>
				</div>
				<button type="submit" class="btn btn--primary" [disabled]="!verificationCode || isSubmitting()">
					@if (isSubmitting()) {
						Verifying&hellip;
					} @else {
						Next
					}
				</button>
			</form>
			<p class="step-footer">
				Didn't receive a code? Check your spam folder, or <a href="https://help.languagebird.com/kb-tickets/new">contact support</a>.
			</p>
		</div>
	}

	<!-- Step 4: Set password -->
	@if (step() === 'set-password') {
		<div class="onboarding-step">
			<h1>You're almost done!</h1>
			<p>Create a secure password that you'll use to log in to LanguageBird.</p>

			@if (errorMessage()) {
				<div class="alert alert--error">{{ errorMessage() }}</div>
				@if (canStartOver()) {
					<div class="error-actions">
						<button type="button" class="btn btn--ghost" (click)="startOver()">Start Over</button>
					</div>
				}
			}

			<form (ngSubmit)="submitPassword()" #form="ngForm">
				<div class="form-field">
					<div class="input-wrapper">
						<input
							id="password"
							[type]="showPassword() ? 'text' : 'password'"
							name="password"
							[(ngModel)]="password"
							autocomplete="new-password"
							required
							minlength="8"
						/>
						<button type="button" class="toggle-visibility" (click)="togglePasswordVisibility()">
							@if (showPassword()) {
								<md-icon icon="untitled-ui/general/eye"></md-icon>
							} @else {
								<md-icon icon="untitled-ui/general/eye-off"></md-icon>
							}
						</button>
					</div>
					<ul class="password-checklist">
						<li [class.met]="pwMinLength">At least 8 characters</li>
						<li [class.met]="pwHasUpper">One uppercase letter</li>
						<li [class.met]="pwHasLower">One lowercase letter</li>
						<li [class.met]="pwHasNumber">One number</li>
						<li [class.met]="pwHasSymbol">One symbol</li>
					</ul>
				</div>
				<button type="submit" class="btn btn--primary" [disabled]="!passwordMeetsRequirements || isSubmitting()">
					@if (isSubmitting()) {
						Setting password&hellip;
					} @else {
						Next
					}
				</button>
			</form>
			<p class="step-footer">Having trouble? <a href="https://help.languagebird.com/kb-tickets/new">Contact support</a> and we'll help you get set up.</p>
		</div>
	}

	<!-- Complete -->
	@if (step() === 'complete') {
		<div class="onboarding-step">
			<h1>Your account is ready!</h1>
			<p>Click below to log in to MyLanguageBird.</p>
			<p>You’ll be prompted to enter your password to continue.</p>
			<p>After that, you can access your portal, complete orientation, set your schedule, and manage your courses.</p>
			<a [href]="loginUrl" class="btn btn--primary">
				Login to MyLanguageBird
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
					<polyline points="15 3 21 3 21 9" />
					<line x1="10" y1="14" x2="21" y2="3" />
				</svg>
			</a>
		</div>
	}
</div>
